import { Thinking } from "@/components/ai/chat/Thinking";
import type { AgentMessageFragment, AgentMessageFragmentType } from "@/hooks/use-stream-conversation";
import { type FC } from "react";
import { FragmentFileModification } from "@/components/ai/FragmentFileModification";
import { FragmentAction } from "@/components/ai/FragmentAction";
import { FragmentText } from "@/components/ai/FragmentText";
import { FragmentEnvironmentCreation } from "@/components/ai/FragmentEnvironmentCreation";
import { FragmentHostAuthentication } from "@/components/ai/FragmentHostAuthentication";
import { FragmentTodoItem } from "@/components/ai/FragmentTodoItem";
import { FragmentCodeAnnotation } from "@/components/ai/FragmentCodeAnnotation";
import { FragmentAgentModeChange } from "@/components/ai/FragmentAgentModeChange";
import { FragmentClarifyingQuestions } from "@/components/ai/FragmentClarifyingQuestions";
import { formatClarifyingQuestionsResponse } from "@/components/ai/clarifying-questions-utils";
import { FragmentNextStepsProposal } from "@/components/ai/FragmentNextStepsProposal";
import { getActiveEnvironmentId } from "@/utils/agent-executions";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useOptionalVSCodeIpcContext } from "@/hooks/use-vscode-ipc";
import { useEnvironmentSettings } from "@/hooks/use-environment-settings";
import { FragmentActionShellScript } from "@/components/ai/FragmentActionShellScript";

export type FragmentProps = {
    agentExecution: PlainAgentExecution;
    fragment: AgentMessageFragment;
    shouldHighlightFragment: boolean;
    onClick?: (block: AgentMessageFragment) => void;
    ignore?: Set<AgentMessageFragmentType>;
    onSendMessage?: (message: string) => void;
};

export const Fragment: FC<FragmentProps> = ({ agentExecution, fragment, shouldHighlightFragment, onClick, ignore, onSendMessage }) => {
    const environmentId = getActiveEnvironmentId(agentExecution);
    const vscodeIpcContext = useOptionalVSCodeIpcContext();
    const { value: viewMode, setValue: setViewMode } = useEnvironmentSettings(
        environmentId || "",
        "viewmode",
        "vscode",
    );

    if (ignore?.has(fragment.type)) {
        return null;
    }

    switch (fragment.type) {
        case "text":
            return (
                <FragmentText
                    key={fragment.id}
                    fragment={fragment}
                    agentExecution={agentExecution}
                    shouldHighlight={shouldHighlightFragment}
                />
            );
        case "action":
            // Currently we don't have a concrete fragment type for user-triggered shell scripts (messages starting with !)
            // we just know they start with that emoji.
            if (fragment.intentDescription.startsWith("📟")) {
                return <FragmentActionShellScript key={`${fragment.type}-${fragment.actionId}`} action={fragment} />;
            } else {
                return <FragmentAction key={`${fragment.type}-${fragment.actionId}`} action={fragment} />;
            }
        case "fileModification":
            return (
                <FragmentFileModification
                    key={`${fragment.type}-${fragment.actionId}`}
                    fragment={fragment}
                    onHandleReveal={() => {
                        if (viewMode !== "vscode") {
                            setViewMode("vscode");
                        }
                        void vscodeIpcContext?.vscodeIpc.current?.openFile(fragment.filePath);
                    }}
                />
            );
        case "environmentCreation":
            return <FragmentEnvironmentCreation environmentId={fragment.environmentId} />;
        case "thinking":
            return (
                <Thinking
                    key={fragment.id}
                    fragment={fragment}
                    onClick={onClick}
                    data-tracking-id="thinking-fragment"
                />
            );
        case "hostAuthenticationRequired":
            return <FragmentHostAuthentication key={fragment.id} fragment={fragment} />;
        case "todoItem":
            return <FragmentTodoItem key={fragment.id} fragment={fragment} />;
        case "codeAnnotation":
            return (
                <FragmentCodeAnnotation
                    key={fragment.id}
                    fragment={fragment}
                    onClick={() => {
                        // Call the original onClick prop for backward compatibility
                        onClick?.(fragment);
                        // Also add VS Code integration
                        if (viewMode !== "vscode") {
                            setViewMode("vscode");
                        }
                        void vscodeIpcContext?.vscodeIpc.current?.openFile(fragment.filePath);
                    }}
                    data-tracking-id="code-annotation-vscode"
                />
            );
        case "agentModeChange":
            return <FragmentAgentModeChange key={fragment.id} fragment={fragment} environmentId={environmentId} />;
        case "clarifyingQuestions":
            return (
                <FragmentClarifyingQuestions
                    key={fragment.id}
                    fragment={fragment}
                    onSubmit={(answers) => {
                        if (onSendMessage) {
                            const response = formatClarifyingQuestionsResponse(fragment.questions, answers);
                            onSendMessage(response);
                        }
                    }}
                />
            );
        case "nextStepsProposal":
            return (
                <FragmentNextStepsProposal
                    key={fragment.id}
                    fragment={fragment}
                    onActionClick={onSendMessage}
                />
            );
        case "userInputSeen":
            // userInputSeen is handled internally and not rendered
            return null;
    }
};
